import type { Order } from '@hero/schema';

export type WaybillRecipientRole =
  | 'send_to_me'
  | 'order_owner'
  | 'client'
  | 'driver'
  | 'sender'
  | 'receiver'
  | 'destination_contact';

export type WaybillRecipientRow = {
  /** Stable row id (role + phone); used as React key and selection key. */
  key: string;
  role: WaybillRecipientRole;
  name: string;
  phone: string;
  /** When true, selected by default (Send to Me). */
  defaultSelected?: boolean;
};

function phoneFromIds(ids?: { type: string; id: string }[]): string | undefined {
  return ids?.find(e => e.type === 'phone')?.id;
}

/**
 * Build deduped recipient rows for the Send waybill modal.
 * Same phone → single row (prefer send_to_me, then earlier roles).
 */
export function buildWaybillRecipientRows(input: {
  order: Order;
  me?: { name?: string; phone?: string };
}): WaybillRecipientRow[] {
  const { order, me } = input;
  const candidates: WaybillRecipientRow[] = [];

  if (me?.phone) {
    candidates.push({
      key: `send_to_me:${me.phone}`,
      role: 'send_to_me',
      name: me.name?.trim() || me.phone,
      phone: me.phone,
      defaultSelected: true,
    });
  }

  if (order.orderOwner?.phone) {
    candidates.push({
      key: `order_owner:${order.orderOwner.phone}`,
      role: 'order_owner',
      name: order.orderOwner.name || order.orderOwner.phone,
      phone: order.orderOwner.phone,
    });
  }

  const clientPhone = phoneFromIds(order.client?.ids);
  if (clientPhone) {
    candidates.push({
      key: `client:${clientPhone}`,
      role: 'client',
      name: order.client?.name || clientPhone,
      phone: clientPhone,
    });
  }

  if (order.driver?.phone) {
    candidates.push({
      key: `driver:${order.driver.phone}`,
      role: 'driver',
      name: order.driver.name || order.driver.phone,
      phone: order.driver.phone,
    });
  }

  if (order.sender?.contact?.phone) {
    candidates.push({
      key: `sender:${order.sender.contact.phone}`,
      role: 'sender',
      name: order.sender.contact.name || order.sender.name || order.sender.contact.phone,
      phone: order.sender.contact.phone,
    });
  }

  if (order.receiver?.contact?.phone) {
    candidates.push({
      key: `receiver:${order.receiver.contact.phone}`,
      role: 'receiver',
      name: order.receiver.contact.name || order.receiver.name || order.receiver.contact.phone,
      phone: order.receiver.contact.phone,
    });
  }

  order.destinations?.forEach((dest, destIndex) => {
    dest.contacts?.forEach((contact, contactIndex) => {
      if (!contact.phone) return;
      candidates.push({
        key: `destination_contact:${destIndex}:${contactIndex}:${contact.phone}`,
        role: 'destination_contact',
        name: contact.name || contact.phone,
        phone: contact.phone,
      });
    });
  });

  const byPhone = new Map<string, WaybillRecipientRow>();
  for (const row of candidates) {
    const existing = byPhone.get(row.phone);
    if (!existing) {
      byPhone.set(row.phone, row);
      continue;
    }
    // Prefer Send to Me when the same phone appears twice.
    if (row.role === 'send_to_me') {
      byPhone.set(row.phone, { ...row, defaultSelected: true });
    }
  }

  return Array.from(byPhone.values());
}
